import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAppStore } from '@/store/appStore';

/**
 * Keeps the app's Pro flag in step with the server when a purchase lands
 * without the app seeing it finish.
 *
 * The store's `user.is_pro` is what unlocks exams and the guide, and it only
 * flips on its own when a purchase returns success in this session. An
 * Android in-app Stripe checkout can end without that — no network on return
 * from the Custom Tab, a sheet closed before Stripe settled, a WebView
 * reloaded under the tab — while the server has already granted Pro. The
 * buyer then sat on a locked app, and tapped Buy into «User already has pro
 * access», until they restarted it.
 */

/**
 * The guide is a Hallouminati feature; core code must not import it statically
 * (scripts/check-product-boundaries.mjs). Loaded on demand and skipped
 * entirely in a GreekBrain-pinned build.
 */
export async function prefetchGuideAfterPurchase(queryClient: QueryClient, locale: string): Promise<void> {
    if (import.meta.env.VITE_APP === 'greekbrain') return;
    if (useAppStore.getState().appType !== 'hallouminati') return;
    const { prefetchAllGuideContent } = await import('@/products/hallouminati/features/CyprusGuide/prefetchGuide');
    await prefetchAllGuideContent(queryClient, locale, true);
}

/**
 * Ask the server whether this user is Pro and, if the app still thinks not,
 * unlock it. Resolves true once the app has the user as Pro.
 */
export async function syncProFromServer(queryClient: QueryClient, reason: string): Promise<boolean> {
    const { user, appType } = useAppStore.getState();
    if (!user) return false;
    if (user.is_pro) return true;

    const response = await apiClient.getProfileData(appType);
    if (!response.success || !response.data?.user?.is_pro) return false;

    // Re-read: the user may have changed (sign-out, purchase) while we waited.
    const current = useAppStore.getState().user;
    if (!current || current.id !== response.data.user.id) return false;
    if (current.is_pro) return true;

    queryClient.setQueryData(['profile', appType], response.data);
    useAppStore.getState().setUser(response.data.user);
    void queryClient.invalidateQueries({ queryKey: ['guide'] });
    void queryClient.invalidateQueries({ queryKey: ['hallouminati-dashboard'] });
    void prefetchGuideAfterPurchase(queryClient, response.data.user.locale || 'en');

    // Each one is a buyer who was looking at a locked app they had paid for.
    Sentry.captureMessage('Pro granted on the server but not in the app — unlocked from a profile refresh', {
        level: 'warning',
        tags: { error_type: 'pro_sync_recovered', reason },
    });
    return true;
}

// After an in-app checkout ends unconfirmed: soon (Stripe settling), then
// spaced out (the webhook, a network that comes back late).
const LATE_PRO_DELAYS_MS = [5_000, 15_000, 30_000, 60_000, 120_000];

let watchingForLatePro = false;

/**
 * Poll the profile for a while after a checkout ended without confirmation, so
 * a payment that lands late unlocks the app where the buyer is instead of on
 * the next restart. A cancelled checkout costs a handful of profile reads.
 */
export function watchForLatePro(queryClient: QueryClient): void {
    if (watchingForLatePro) return;
    watchingForLatePro = true;

    void (async () => {
        try {
            for (const wait of LATE_PRO_DELAYS_MS) {
                await new Promise<void>((resolve) => setTimeout(resolve, wait));
                if (useAppStore.getState().user?.is_pro) return;
                if (await syncProFromServer(queryClient, 'checkout_unconfirmed')) return;
            }
        } finally {
            watchingForLatePro = false;
        }
    })();
}
